import {
    Button,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    styled,
} from '@mui/material';
import AppModal from 'components/AppModal';
import FlexBox from 'components/flexbox/FlexBox';
import AppTextField from 'components/input-fields/AppTextField';
import Scrollbar from 'components/ScrollBar';
import { H2, H6 } from 'components/Typography';
import { useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import axiosInstance from 'utils/axios';
import * as Yup from 'yup';

const StyledAppModal = styled(AppModal)(({ theme }) => ({
    maxWidth: 600,
    minWidth: 450,
    [theme.breakpoints.down(325)]: {
        maxWidth: '100%',
    },
}));

const FormModal = ({ open, onClose, edit, data, onSubmissionSuccess }) => {
    const [workshopTypeList, setWorkshopTypeList] = useState([]);
    const [eventList, setEventList] = useState([]);
    const initialValues = useMemo(() => {
        return {
            name: data?.name || '',
            workshop_type: data?.workshop_type?.id || '',
            capacity: data?.capacity || '',
        };
    }, [data]);
    const fieldValidationSchema = Yup.object().shape({
        name: Yup.string().min(3, 'Too Short').required('Name is Required!'),
        workshop_type: Yup.string().required('Workshop type is Required!'),
        event: Yup.string().required('Event is Required!'),
        capacity: Yup.number(),
    });
    const url = edit ? `api/workshops/${data.id}` : 'api/workshops';
    const requestMethod = edit ? 'patch' : 'post';
    const { values, errors, handleChange, handleSubmit, touched, setValues } =
        useFormik({
            initialValues,
            validationSchema: fieldValidationSchema,
            onSubmit: async (values, { setSubmitting }) => {
                try {
                    await axiosInstance[requestMethod](url, values);
                    onSubmissionSuccess();
                    onClose();
                    toast.success('Updated Successfully');
                } catch (error) {
                    console.log(error);
                } finally {
                    setSubmitting(false);
                }
            },
        });
    useEffect(() => {
        setValues(initialValues);
        // eslint-disable-next-line
    }, [setValues, initialValues]);
    return (
        <StyledAppModal open={open} handleClose={onClose}>
            <H2 mb={2}>{edit ? 'Edit Workshop' : 'Add New Workshop'}</H2>
            <form onSubmit={handleSubmit}>
                <Scrollbar style={{ maxHeight: 400 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <H6 mb={1}>Workshop Name</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="name"
                                placeholder="Name"
                                value={values.name}
                                onChange={handleChange}
                                error={Boolean(errors.name && touched.name)}
                                helperText={touched.name && errors.name}
                            />
                        </Grid>

                        <Grid item xs={2}>
                            <H6 mb={1}>Capacity</H6>
                            <AppTextField
                                fullWidth
                                size="small"
                                name="capacity"
                                placeholder="capacity"
                                onChange={handleChange}
                                value={values.capacity}
                                error={Boolean(
                                    errors.capacity && touched.capacity,
                                )}
                                helperText={touched.capacity && errors.capacity}
                            />
                        </Grid>

                        <Grid item xs={4}>
                            <H6 mb={1}>Workshop Type</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'workshop_type'}
                                fullWidth
                                value={values.workshop_type}
                                onChange={handleChange}
                            >
                                {workshopTypeList &&
                                    workshopTypeList.map((item, key) => (
                                        <MenuItem key={key} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {touched.workshop_type && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.workshop_type}
                                </FormHelperText>
                            )}
                        </Grid>
                        <Grid item xs={6}>
                            <H6 mb={1}>Event</H6>
                            <Select
                                size="small"
                                variant="outlined"
                                name={'event'}
                                fullWidth
                                value={values.event}
                                onChange={handleChange}
                            >
                                {eventList &&
                                    eventList.map((item, key) => (
                                        <MenuItem key={key} value={item.id}>
                                            {item.name}
                                        </MenuItem>
                                    ))}
                            </Select>
                            {touched.event && (
                                <FormHelperText
                                    error
                                    sx={{
                                        ml: '14px',
                                        fontSize: '0.75rem',
                                        fontWeight: 400,
                                    }}
                                >
                                    {errors.event}
                                </FormHelperText>
                            )}
                        </Grid>
                    </Grid>
                </Scrollbar>

                <FlexBox justifyContent="flex-end" gap={2} marginTop={4}>
                    <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        onClick={onClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        fullWidth
                        size="small"
                        type="submit"
                        variant="contained"
                    >
                        Save
                    </Button>
                </FlexBox>
            </form>
        </StyledAppModal>
    );
};

export default FormModal;
